/**
 * Add a team (`/desks/new`, the sheet in wide mode): the pure answers the
 * sheet prints and sends.
 *
 * One Team at two sizes, and three ways a full Team reads its tickets. Each
 * combination is a request an existing builder already makes, so the
 * `POST /api/desks` contract does not change:
 *
 *   Pull request reviews            `buildReviewTeamBody`: Proctor alone, one
 *                                   review line, the repositories attached
 *   Full agent team, Assigned to me `buildStartTeamBody`: the person's tickets
 *                                   (`ticketsAssignedTo`), Bailey proposing
 *   Full agent team, From a filter  the pipeline's catalog defaults with the
 *                                   tracker filter attached (`backlogMode:
 *                                   "backlog"`)
 *   Full agent team, Only what I    the same pipeline with Bailey on the Team
 *   hand it                         and his propose line off, no filter
 *
 * Every size carries the picked repositories, which the create route adds to
 * the workspace before it attaches them.
 */
import { type CreateTeamLineInput, type DeskAttachmentInput } from "@yagni/shared";

import { firstNameOf } from "../lib/names.js";
import type { ReadsMode } from "../teams/ReadsChoice.js";
import { buildReviewTeamBody } from "../work/reviewTeamRequest.js";
import { buildStartTeamBody, type StartTracker } from "../work/startMyTeamRequest.js";
import { CREATE_TEAM_LINE_KINDS, defaultLines } from "./createTeamLines.js";

export type TeamSize = "reviews" | "full";

/** The order the radios are offered in. Full is the default. */
export const TEAM_SIZES: readonly TeamSize[] = ["reviews", "full"];

/**
 * `?size=`: `reviews`, else the full Team. The three-size page's `mine` and
 * `backlog` are both a full Team now (`readsFromSizeParam` keeps what they
 * meant), so an older link or GitHub install return still lands.
 */
export function parseTeamSize(value: string | null): TeamSize {
  return value === "reviews" ? "reviews" : "full";
}

/** What an older `?size=` said about where the tickets come from. */
export function readsFromSizeParam(value: string | null): ReadsMode | null {
  if (value === "mine") return "me";
  if (value === "backlog") return "filter";
  return null;
}

export const TEAM_SIZE_COPY: Record<TeamSize, { title: string; body: string }> = {
  reviews: {
    title: "Pull request reviews",
    body: "Proctor reviews every pull request on the repositories above and posts one review. Nothing else runs.",
  },
  full: {
    title: "Full agent team",
    body: "Bailey proposes, Reeve plans, Wright builds, Proctor reviews, Fletcher tests, Harper posts. Every gate starts supervised.",
  },
};

const FULL_TEAM_CAST =
  "Bailey proposes, Reeve plans, Wright builds, Proctor reviews, Fletcher tests, Harper posts.";

/**
 * A size's line under its radio. On a workspace Managed by YAGNI
 * (`GET /api/desks/create-defaults`), a Team starts Autonomous, so the full
 * Team does not say its gates start supervised.
 */
export function teamSizeBody(size: TeamSize, startsAutonomous: boolean): string {
  if (size === "full" && startsAutonomous) {
    return `${FULL_TEAM_CAST} YAGNI runs this Team: proposing, planning, building and reviewing start autonomous.`;
  }
  return TEAM_SIZE_COPY[size].body;
}

/** The sheet foot's sentence for a full Team, split so the second half can be bold. */
export function fullTeamFootCopy(startsAutonomous: boolean): { lead: string; bold: string } {
  return startsAutonomous
    ? {
        lead: "YAGNI runs this Team, so it starts autonomous.",
        bold: "Merging is still a person's click.",
      }
    : {
        lead: "Every gate starts supervised.",
        bold: "Nothing is planned or built until you approve it.",
      };
}

/** `GET /api/desks/create-defaults`: whether a Team created now starts autonomous. False on anything else. */
export function readStartsAutonomous(payload: unknown): boolean {
  return (payload as { startsAutonomous?: unknown } | null)?.startsAutonomous === true;
}

/** A repository the GitHub App installation covers (`GET /api/repos/available`). */
export interface AvailableRepo {
  fullName: string;
  owner?: string;
  name?: string;
  defaultBranch?: string;
  /** Already added to the workspace. */
  added?: boolean;
  language?: string | null;
  openPullRequests?: number | null;
}

/** "TypeScript · 14 open PRs", whichever halves are known. */
export function repoFacts(repo: AvailableRepo): string {
  const parts: string[] = [];
  if (repo.language) parts.push(repo.language);
  if (typeof repo.openPullRequests === "number") {
    parts.push(`${repo.openPullRequests} open ${repo.openPullRequests === 1 ? "PR" : "PRs"}`);
  }
  return parts.join(" · ");
}

/** `checkout-web` → "Checkout web": a Team name from a repository name. */
export function nameFromRepo(ref: string): string {
  const base = (ref.split("/").pop() ?? ref).replace(/[-_.]+/g, " ").trim();
  if (!base) return "";
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/** The Team a person's own tickets run on. */
export function personalTeamName(displayName: string | null | undefined): string {
  const first = firstNameOf(displayName);
  return first ? `${first}'s team` : "My team";
}

/**
 * The name Create gives the Team when the Name box is left blank: the
 * person's for a full Team on their own tickets; otherwise the first
 * repository, then the first words of Owns.
 */
export function teamNameFor(args: {
  size: TeamSize;
  reads: ReadsMode;
  viewerName: string | null;
  repos: readonly string[];
  owns: string;
}): string {
  if (args.size === "full" && args.reads === "me") return personalTeamName(args.viewerName);
  const fromRepo = args.repos[0] ? nameFromRepo(args.repos[0]) : "";
  if (fromRepo) return fromRepo;
  const words = args.owns
    .trim()
    .split(/\s+/)
    .slice(0, 3)
    .join(" ")
    .replace(/[.,;:]+$/, "");
  return words || "New team";
}

function repoAttachments(
  refs: readonly string[],
): Array<{ kind: "repo"; source: "github"; ref: string }> {
  return refs.map((ref) => ({ kind: "repo", source: "github", ref }));
}

/** Every offered line at its catalog default, with Bailey's propose line set as asked. */
function pipeline(backlogMode: "proactive" | "backlog", propose: boolean): CreateTeamLineInput[] {
  const defaults = defaultLines({
    githubOn: true,
    backlogMode,
    trackerPicked: backlogMode === "backlog",
    fletcherAvailable: false,
  });
  return CREATE_TEAM_LINE_KINDS.map((kind) => ({
    kind,
    active: kind === "pm.propose" ? propose : defaults[kind],
  }));
}

export interface CreateTeamInput {
  size: TeamSize;
  reads: ReadsMode;
  name: string;
  /** Whose tickets an Assigned to me Team works from (a workspace user id). */
  person: string | null;
  repos: readonly string[];
  /** The tracker the Team reads: the filter's, or the workspace's for the other two. */
  tracker: StartTracker | null;
  /** The filter, as the attachment Create sends (From a filter only). */
  backlog: DeskAttachmentInput | null;
  owns: string;
}

/**
 * Why the request cannot be built yet, in the sheet's words; null when it can.
 * `filterReady` is whether the typed filter reads as something to attach.
 */
export function createBlockedBy(
  input: Omit<CreateTeamInput, "backlog"> & { filterReady: boolean },
): string | null {
  if (input.repos.length === 0) return "Pick at least one repository for the Team to work on.";
  if (input.size === "reviews") return null;
  if (input.reads === "me" && !input.person) return "Choose whose tickets the Team works from.";
  if (input.reads === "filter") {
    if (!input.filterReady) return "Paste the JQL, a filter link or issue keys for Bailey to read.";
    if (!input.owns.trim()) return "Say what this Team owns in a sentence or two.";
  }
  return null;
}

/**
 * The one `POST /api/desks` body for a size and a Reads choice, from the
 * builders that already made each of them. `null` when it is missing what it
 * needs (`createBlockedBy` says what).
 */
export function buildCreateTeamBody(input: CreateTeamInput): object | null {
  if (input.repos.length === 0) return null;
  const owns = input.owns.trim();
  if (input.size === "reviews") {
    const body = buildReviewTeamBody(input.repos);
    return { ...body, name: input.name, ...(owns ? { goal: owns } : {}) };
  }
  if (input.reads === "me") {
    // No tracker is fine: the Team works from tickets started in the rail.
    if (!input.person) return null;
    const body = buildStartTeamBody({
      viewer: { id: input.person },
      tracker: input.tracker,
      repoRef: input.repos[0]!,
    });
    return {
      ...body,
      name: input.name,
      ...(owns ? { prompt: owns } : {}),
      attachments: repoAttachments(input.repos),
      ticketsAssignedTo: [input.person],
    };
  }
  if (input.reads === "filter") {
    if (!input.backlog || !owns) return null;
    return {
      name: input.name,
      prompt: owns,
      sources: ["github", input.backlog.source],
      backlogMode: "backlog",
      attachments: [...repoAttachments(input.repos), input.backlog],
      lines: pipeline("backlog", true),
    };
  }
  // Only what I hand it: Bailey is on the Team so the choice can change later
  // under Bailey, and his propose line is off, so he never picks on his own.
  return {
    name: input.name,
    prompt: owns || "Work the tickets I hand it.",
    sources: input.tracker ? ["github", input.tracker] : ["github"],
    attachments: repoAttachments(input.repos),
    workers: ["bailey"],
    lines: pipeline("proactive", false),
  };
}

/** What the 201 says did not land, one line each. Empty: it all went through. */
export function createNotes(payload: unknown): string[] {
  const setup = (payload as { setup?: Record<string, unknown> } | null)?.setup;
  if (!setup || typeof setup !== "object") return [];
  const notes: string[] = [];
  const attachFailed = Array.isArray(setup.attachmentsFailed) ? setup.attachmentsFailed : [];
  for (const entry of attachFailed as Array<{ ref?: unknown; error?: unknown }>) {
    if (typeof entry?.ref === "string") {
      notes.push(
        `${entry.ref} could not be attached${typeof entry.error === "string" ? `: ${entry.error}` : "."}`,
      );
    }
  }
  const workers = setup.workers as
    | { failed?: Array<{ slug?: unknown; error?: unknown }> }
    | undefined;
  for (const entry of workers?.failed ?? []) {
    if (typeof entry?.slug === "string") {
      const who = entry.slug.charAt(0).toUpperCase() + entry.slug.slice(1);
      notes.push(
        `${who} did not join${typeof entry.error === "string" ? `: ${entry.error}` : "."}`,
      );
    }
  }
  const lines = setup.lines as { failed?: Array<{ kind?: unknown; error?: unknown }> } | undefined;
  for (const entry of lines?.failed ?? []) {
    if (typeof entry?.kind === "string") {
      notes.push(
        `A line did not switch on${typeof entry.error === "string" ? `: ${entry.error}` : "."}`,
      );
    }
  }
  return notes;
}
